import { useState, type MouseEvent } from 'react';
import { Link, useParams, useRouter, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ticketDetailQuery } from './tickets-queries';
import { ChangeClientDialog } from './ChangeClientDialog';
import { TicketDetailHeader } from './detail-header';
import { InfoGeneralTab } from './detail-info-tab';
import { ApontamentosTab } from './detail-apontamentos-tab';
import { ValoresTab } from './detail-valores-tab';
import { ComunicacaoTab } from './detail-comunicacao-tab';
import { ConversasTab } from './detail-conversas-tab';
import { HistoricoTab } from './detail-historico-tab';
import { ChecklistsTab } from './detail-checklists';
import { ServiceOrdersTab } from '@/features/service-orders/detail-service-orders-tab';
import { ticketsBack } from './tickets-back';

const TAB_TRIGGER_CLASSES =
  'h-10 flex-none rounded-none px-1 text-sm font-medium data-[state=active]:text-primary after:h-0.5 after:bg-primary group-data-[orientation=horizontal]/tabs:after:bottom-0';

/**
 * Detalhe do ticket estilo Tiflux: card header branco "#N - TÍTULO" + chip de
 * status + "Ações do ticket", abas com ink teal e conteúdo por aba.
 */
export function TicketDetailPage() {
  const params = useParams({ strict: false }) as { ticketNumber?: string };
  const ticketNumber = Number(params.ticketNumber ?? 0);

  const detailQ = useQuery({
    ...ticketDetailQuery(ticketNumber),
    // Poll while any timer is running so worked time / running badges stay fresh.
    refetchInterval: (query) =>
      (query.state.data?.runningTimerUserIds.length ?? 0) > 0 ? 30_000 : false,
  });

  const [tab, setTab] = useState('info');
  const [changeClientOpen, setChangeClientOpen] = useState(false);

  // spec 018: voltar preserva a lista — o link aponta para a última lista
  // desta aba; se ela é a entrada anterior do histórico, volta o navegador
  // (a rolagem vem junto). Clique com modificador segue sendo link.
  const router = useRouter();
  const historyIndex = useRouterState({ select: (s) => s.location.state.__TSR_index });
  const back = ticketsBack(historyIndex);
  const onBack = (e: MouseEvent<HTMLAnchorElement>) => {
    if (!back.viaHistory || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    router.history.back();
  };

  if (detailQ.isPending) return <DetailSkeleton />;

  if (detailQ.isError) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 rounded-lg border bg-card p-12 text-center shadow-sm">
        <p className="text-base font-semibold">Ticket não encontrado</p>
        <p className="text-sm text-muted-foreground">
          O ticket #{ticketNumber} não existe ou você não tem acesso a ele.
        </p>
        <Link
          to="/tickets"
          search={back.search}
          onClick={onBack}
          className="text-sm font-medium text-primary hover:underline"
        >
          Voltar para tickets
        </Link>
      </div>
    );
  }

  const ticket = detailQ.data;

  return (
    <div className="space-y-3">
      <Link
        to="/tickets"
        search={back.search}
        onClick={onBack}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="size-3.5" />
        Tickets
      </Link>

      <Tabs value={tab} onValueChange={setTab} className="gap-4">
        <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
          <TicketDetailHeader ticket={ticket} onChangeClient={() => setChangeClientOpen(true)} />
          <TabsList
            variant="line"
            className="w-full justify-start gap-6 rounded-none p-0 px-4 group-data-[orientation=horizontal]/tabs:h-11"
          >
            <TabsTrigger value="info" className={TAB_TRIGGER_CLASSES}>
              Informações gerais
            </TabsTrigger>
            <TabsTrigger value="apontamentos" className={TAB_TRIGGER_CLASSES}>
              Apontamentos
            </TabsTrigger>
            <TabsTrigger value="valores" className={TAB_TRIGGER_CLASSES}>
              Valores
            </TabsTrigger>
            <TabsTrigger value="comunicacao" className={TAB_TRIGGER_CLASSES}>
              Comunicação
            </TabsTrigger>
            <TabsTrigger value="conversas" className={TAB_TRIGGER_CLASSES}>
              Conversas
            </TabsTrigger>
            <TabsTrigger value="checklists" className={TAB_TRIGGER_CLASSES}>
              Checklists
            </TabsTrigger>
            <TabsTrigger value="ordens-de-servico" className={TAB_TRIGGER_CLASSES}>
              Ordens de serviço
            </TabsTrigger>
            <TabsTrigger value="historico" className={TAB_TRIGGER_CLASSES}>
              Histórico
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="info">
          <InfoGeneralTab ticket={ticket} onChangeClient={() => setChangeClientOpen(true)} />
        </TabsContent>
        <TabsContent value="apontamentos">
          <ApontamentosTab ticket={ticket} />
        </TabsContent>
        <TabsContent value="valores">
          <ValoresTab ticket={ticket} />
        </TabsContent>
        <TabsContent value="comunicacao">
          <ComunicacaoTab ticket={ticket} />
        </TabsContent>
        <TabsContent value="conversas">
          <ConversasTab ticket={ticket} />
        </TabsContent>
        <TabsContent value="checklists">
          <ChecklistsTab ticket={ticket} />
        </TabsContent>
        <TabsContent value="ordens-de-servico">
          <ServiceOrdersTab ticket={ticket} />
        </TabsContent>
        <TabsContent value="historico">
          <HistoricoTab ticketNumber={ticketNumber} />
        </TabsContent>
      </Tabs>

      {changeClientOpen ? (
        <ChangeClientDialog
          ticketNumber={ticketNumber}
          currentClientName={ticket.client.name}
          onClose={() => setChangeClientOpen(false)}
        />
      ) : null}
    </div>
  );
}

/** Skeleton que espelha o layout carregado (header + grid 3 colunas). */
function DetailSkeleton() {
  return (
    <div className="space-y-3">
      <Skeleton className="h-4 w-16" />
      <div className="rounded-lg border bg-card p-4 shadow-sm">
        <div className="flex items-center justify-between gap-4">
          <Skeleton className="h-6 w-2/5" />
          <Skeleton className="h-8 w-36" />
        </div>
        <div className="mt-4 flex gap-6">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-20" />
        </div>
      </div>
      <div className="grid items-start gap-4 lg:grid-cols-[300px_minmax(0,1fr)] xl:grid-cols-[300px_minmax(0,1fr)_340px]">
        <Skeleton className="h-56 rounded-lg" />
        <Skeleton className="h-72 rounded-lg" />
        <Skeleton className="h-80 rounded-lg" />
      </div>
    </div>
  );
}
